// src/components/Footer.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Papa from 'papaparse'; // Import PapaParse
import './Footer.css';
import SmartlightLogoWhite from '../Images/SmartlightLogoWhite.png'; 
import { FaInstagram, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  // --- 1. State for dynamic contact info ---
  const [footerInfo, setFooterInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // --- 2. useEffect to fetch data from Google Sheet ---
  useEffect(() => {
    // PASTE YOUR CONTACT INFO GOOGLE SHEET CSV URL HERE
    const CONTACT_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vTzIyZxxb_iHDFdrVrA6pvkY-Xqo27Fbv1U44HP_m7S4gwVLWfQJMf2juINo1p1CwTjExQN_3EbaqwJ/pub?output=csv";

    Papa.parse(CONTACT_SHEET_URL, {
        download: true,
        header: true,
        complete: (results) => {
            // The contact info is the first (and only) row of data
            if (results.data && results.data.length > 0) {
                setFooterInfo(results.data[0]);
            } else {
                console.error("No data found in Google Sheet for Footer.");
            }
            setIsLoading(false);
        },
        error: (err) => {
            console.error("Error fetching footer data:", err);
            setIsLoading(false); // Set loading to false even on error
        },
    });
  }, []); // The empty [] ensures this runs only once

  // --- 3. Create the dynamic WhatsApp URL ---
  // The 'replace' function is a safety measure to remove any non-numeric characters.
  const whatsappUrl = footerInfo?.whatsappNumber 
    ? `https://wa.me/${footerInfo.whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(footerInfo.whatsappMessage)}`
    : '#';

  // Render a minimal footer while loading to prevent errors and layout shifts
  if (isLoading) {
    return (
        <footer className="footer">
            <div className="footer-container">
                <div className="footer-bottom">
                    <p className="copyright">© {currentYear} SmartLight. All rights reserved.</p>
                </div>
            </div>
        </footer>
    );
  }

  // The main footer, rendered after data has loaded
  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          {/* Section 1: Brand Info (now dynamic) */}
          <div className="footer-section">
            <h3 className="footer-brand">
              <img src={SmartlightLogoWhite} alt="Smartlight Logo" className="footer-logo" />
            </h3>
            <p className="footer-description">
              {footerInfo?.footerDescription || "Illuminating spaces with innovative lighting solutions."}
            </p>
            <div className="social-links">
              {/* These links only render if the URL exists in the Google Sheet */}
              {footerInfo?.instagramUrl && (
                  <a href={footerInfo.instagramUrl} className="social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <FaInstagram />
                  </a>
              )}
              {footerInfo?.linkedinUrl && (
                  <a href={footerInfo.linkedinUrl} className="social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <FaLinkedinIn />
                  </a>
              )}
              {footerInfo?.whatsappNumber && (
                  <a href={whatsappUrl} className="social-link" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp />
                  </a>
              )}
            </div>
          </div>

          {/* Section 2: Quick Links (These are internal app routes, so they remain static) */}
          <div className="footer-section">
            <h4 className="footer-title">Quick Links</h4>
            <ul className="footer-links">
              <li><Link to="/" className="footer-link">Home</Link></li>
              <li><Link to="/services" className="footer-link">Services</Link></li>
              <li><Link to="/about" className="footer-link">About Us</Link></li>       
              <li><Link to="/contactus" className="footer-link">Contact</Link></li>
              <li><Link to="/products" className="footer-link">Product</Link></li>
              <li><Link to="/projects" className="footer-link">Project</Link></li>
            </ul>
          </div>

          {/* Section 3: Services (Static, as these are likely fixed) */}
          <div className="footer-section">
            <h4 className="footer-title">Services</h4>
            <ul className="footer-links">
              <li><Link to="/services" className="footer-link">LED Lighting</Link></li>
              <li><Link to="/services" className="footer-link">Smart Home</Link></li>
              <li><Link to="/services" className="footer-link">Commercial Lighting</Link></li>
              <li><Link to="/services" className="footer-link">Architectural lighting</Link></li>
              <li><Link to="/services" className="footer-link">Decorative lighting</Link></li>
              <li><Link to="/services" className="footer-link">Wireless automation</Link></li>
            </ul>
          </div>

          {/* Section 4: Contact Info (now dynamic) */}
          <div className="footer-section">
            <h4 className="footer-title">Contact Info</h4>
            <div className="contact-info">
              {footerInfo?.address} <br />
              {footerInfo?.phone2} 
              {/* Only show the second phone number if it exists in the sheet */}
              {footerInfo?.phone3 && <> <br /> {footerInfo.phone3} </>}
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="copyright">
              © {currentYear} SmartLight. All rights reserved.
            </p>
            <div className="footer-bottom-links">
              <Link to="/privacy-policy" className="footer-bottom-link">Privacy Policy</Link>
              <Link to="/terms-of-service" className="footer-bottom-link">Terms of Service</Link>
              <Link to="/cookie-policy" className="footer-bottom-link">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}